/**
 * **Subsystem 4 — the outbox is an audit receipt, never a command queue.**
 *
 * This is the single most important rule in P16, stated three times in three documents (§10 row
 * 7, PEC-11, `device-cache.md`) because it is the failure that would matter:
 *
 * > **Reconnect must never replay `CMD:OpenN/P`.** A queued receipt is a record that a gate
 * > *already physically opened*. It is not an instruction to open one — before or after erasure
 * > (D69).
 *
 * So read this file for what it cannot express. A {@link QueuedReceipt} has no command field, no
 * `pedestrian` flag, no gate address, and no credential; there is no `replay()`, and
 * {@link drainOutbox} takes an uploader rather than a general callback. **A row here cannot be
 * turned into an open without adding a field to it**, which is the shape that makes the rule
 * survive the next person in this file.
 *
 * The flow (P16 §6):
 *
 *  1. A BLE open completes locally while the phone is offline. **Show local success
 *     immediately** (PEC-11) — that is the screen's job, and it does not wait for this module.
 *  2. **Queue a receipt** — durable, account-scoped, carrying a client-generated reconciliation
 *     id and the **physical timestamp**.
 *  3. **On reconnect, upload it** (startup contract step 8).
 *  4. **Delete only once acknowledged.** Retry until then.
 *
 * **`device-cache.md`'s field list is the whole schema and is quoted rather than paraphrased:**
 * *"Keep only gate ID, action, result, physical timestamp, transport, client-generated
 * reconciliation ID, and the minimum device/session reference."* Immediately before it: **"Store
 * no BLE password."**
 */

import type { KeyValueStore } from '../device-cache/store'
import type { ReceiptUpload } from './api'

/**
 * The stored row.
 *
 * **`transport` is a literal rather than a free field**, and `action` likewise: Stage-1 resident
 * BLE has exactly one action and one transport, and a widened type here is the first half of
 * turning this queue into a command queue. When P20 adds the native leaf it inherits this row
 * unchanged (seams: *"never forked"*), because the leaf changes how a gate is reached and not
 * what a receipt is.
 *
 * **No BLE password, no gate address, no broker code.** The gate is named by its `GateId`, which
 * grants nothing on its own (`contracts.md` §1b) and is already in the snapshot beside it.
 */
export interface QueuedReceipt {
  /** Bumped if this row's shape ever changes. The store does **not** discard it on a schema
   *  bump (`store.ts`'s `DERIVED_STORES`), so a future change migrates rather than drops. */
  readonly version: 1
  readonly reconciliationId: string
  readonly gateId: string
  readonly action: 'open'
  readonly transport: 'ble'
  readonly result: 'success' | 'failure'
  readonly failureReason?: 'auth-failed' | 'timeout' | 'disconnected' | 'unavailable'
  /** **The physical open timestamp** (D25) — not when this row was written, and not the reconnect. */
  readonly occurredAt: string
  /** 🔸 Diagnostic. The client's own measurement of the BLE exchange. */
  readonly latencyMs?: number
  /** How many upload attempts this row has survived. Bookkeeping; never sent. */
  readonly attempts: number
}

/**
 * **Account-scoped, and the account is in the key rather than in the row.**
 *
 * *"Signing out and signing in as another person never reveals the first person's gates"* is the
 * cache's rule, and a receipt is stronger than a gate name: it says a specific person opened a
 * specific gate at a specific minute. `purgeOtherAccounts` walks keys by prefix, so the partition
 * has to be readable from the key alone.
 */
export function receiptKey(stage: string, userId: string, reconciliationId: string): string {
  return `${stage}:${userId}:${reconciliationId}`
}

const partitionPrefix = (stage: string, userId: string): string => `${stage}:${userId}:`

/**
 * A client-generated reconciliation id — **the identity of one physical opening** (PEC-12).
 *
 * `globalThis.crypto` is the CSPRNG on both sides (`packages/core` relies on the same fact), and
 * a UUID satisfies the server's shape rule with room to spare. The `ble-` prefix is not
 * decoration: it makes a reconciliation id visibly *not* an idempotency key in a log or a
 * network trace, and those two suppress different things — one a duplicate request, one a
 * duplicate opening.
 */
export function newReconciliationId(): string {
  return `ble-${globalThis.crypto.randomUUID()}`
}

/**
 * Step 2 — queue a receipt for an opening that **already happened**.
 *
 * **It is called after a known BLE result and never before one** (PEC-11: *"Create only after a
 * known BLE result"*). A receipt written speculatively, before the controller answered, would be
 * a record of something that may not have occurred — and on reconnect it would become an
 * Activity row asserting an opening nobody witnessed, which is the same class of lie D134/D135
 * exist to forbid one layer up.
 */
export async function queueReceipt(
  store: KeyValueStore,
  stage: string,
  userId: string,
  receipt: Omit<QueuedReceipt, 'version' | 'attempts'>,
): Promise<void> {
  const row: QueuedReceipt = { ...receipt, version: 1, attempts: 0 }
  await store.put('outbox', receiptKey(stage, userId, receipt.reconciliationId), row)
}

/** Every queued receipt for one account, oldest opening first — so Activity fills in order. */
export async function readOutbox(
  store: KeyValueStore,
  stage: string,
  userId: string,
): Promise<readonly QueuedReceipt[]> {
  const prefix = partitionPrefix(stage, userId)
  const rows: QueuedReceipt[] = []
  for (const key of await store.keys('outbox')) {
    if (!key.startsWith(prefix)) continue
    const row = await store.get<QueuedReceipt>('outbox', key)
    if (isQueuedReceipt(row)) rows.push(row)
  }
  return rows.sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
}

/**
 * A row this build understands. A row it does not is **left in place, not repaired and not
 * deleted** — it was written by some other version of this app and belongs to whichever one can
 * read it. Dropping it here would discard an audit record to tidy a type.
 */
function isQueuedReceipt(row: unknown): row is QueuedReceipt {
  if (typeof row !== 'object' || row === null) return false
  const candidate = row as Partial<QueuedReceipt>
  return (
    candidate.version === 1 &&
    typeof candidate.reconciliationId === 'string' &&
    typeof candidate.gateId === 'string' &&
    typeof candidate.occurredAt === 'string' &&
    (candidate.result === 'success' || candidate.result === 'failure')
  )
}

/** Step 4 — the acknowledged row goes, and only then. */
export async function deleteReceipt(
  store: KeyValueStore,
  stage: string,
  userId: string,
  reconciliationId: string,
): Promise<void> {
  await store.remove('outbox', receiptKey(stage, userId, reconciliationId))
}

/** The wire subset of a stored row. Bookkeeping fields stay behind. */
export function toUpload(receipt: QueuedReceipt): ReceiptUpload {
  return {
    reconciliationId: receipt.reconciliationId,
    occurredAt: receipt.occurredAt,
    result: receipt.result,
    ...(receipt.failureReason === undefined ? {} : { failureReason: receipt.failureReason }),
    ...(receipt.latencyMs === undefined ? {} : { latencyMs: receipt.latencyMs }),
  }
}

/** What one upload attempt concluded. `retry` is the only outcome that leaves the row in place. */
export type UploadOutcome = 'acknowledged' | 'retry'

/**
 * Step 3 — **startup/reconnect contract step 8**: *"Reconcile receipts for BLE actions that
 * already completed; never replay an open."*
 *
 * @param upload one attempt for one receipt. The caller supplies it, so this module names no
 *   endpoint and holds no session — and so the *only* thing a drain can do to a receipt is send
 *   it to whatever the caller passes. There is no path from here to a gate command.
 *
 * **Sequential rather than concurrent, deliberately.** These are audit rows, the queue is short
 * by construction (an offline BLE open is a rare event), and every gate-scoped route shares one
 * per-caller ceiling (D68/D90) — so a burst of parallel uploads would spend the budget the next
 * real action needs, to save a second on something nobody is watching.
 *
 * **A failed attempt stops the drain rather than walking the rest of the queue.** The realistic
 * failure is that reception is gone again, and the remaining rows would fail identically; trying
 * them anyway is a burst of doomed requests at the exact moment the link is worst.
 */
export async function drainOutbox(
  store: KeyValueStore,
  stage: string,
  userId: string,
  upload: (receipt: QueuedReceipt) => Promise<UploadOutcome>,
): Promise<{ readonly acknowledged: number; readonly remaining: number }> {
  const queued = await readOutbox(store, stage, userId)
  let acknowledged = 0

  for (const receipt of queued) {
    let outcome: UploadOutcome
    try {
      outcome = await upload(receipt)
    } catch {
      // An uploader that threw is a link that failed. Same answer as `retry`, and the row stays.
      break
    }
    if (outcome !== 'acknowledged') {
      await store.put('outbox', receiptKey(stage, userId, receipt.reconciliationId), {
        ...receipt,
        attempts: receipt.attempts + 1,
      })
      break
    }
    await deleteReceipt(store, stage, userId, receipt.reconciliationId)
    acknowledged += 1
  }

  return { acknowledged, remaining: queued.length - acknowledged }
}

/**
 * **Privacy erasure and the gate-removal rule, which point in opposite directions.**
 *
 * `device-cache.md` is explicit on both, and they are easy to collapse into one:
 *
 *  - **Gate removal:** *"Preserve an already-created offline BLE audit receipt long enough to
 *    reconcile or route it through the privacy-safe recovery policy; never turn it into a
 *    command."* So removing a gate does **not** clear its receipts, and nothing in `purge.ts`'s
 *    `withoutGate` reaches this store.
 *  - **Privacy erasure:** *"An unsynced completed-BLE receipt is discarded, not reconciled
 *    (D69)."* That is `purgeEverything`, which destroys the whole database — this store with it.
 *
 * This function is the narrow middle case: **sign-out on a shared device.** The signed-out
 * account's receipts leave with its snapshot, because a receipt is stronger evidence about a
 * person than a gate name is, and the next person to use the phone must not hold either.
 *
 * 🔸 The cost is real and accepted: signing out with an unsynced receipt loses that Activity row.
 * The alternative — keeping a record of one person's movements on a device they have handed
 * back — is worse, and the same trade `purgeAccount` already makes for the snapshot.
 */
export async function purgeAccountOutbox(store: KeyValueStore, stage: string, userId: string): Promise<void> {
  const prefix = partitionPrefix(stage, userId)
  for (const key of await store.keys('outbox')) {
    if (key.startsWith(prefix)) await store.remove('outbox', key)
  }
}

/** Account switch — everything that is not this account's, matching `purgeOtherAccounts`. */
export async function purgeOtherAccountOutboxes(
  store: KeyValueStore,
  stage: string,
  keepUserId: string,
  /** Consulted before every removal — see `purgeOtherAccounts`'s note on the boot backstop. */
  proceed: () => boolean = () => true,
): Promise<void> {
  const keep = partitionPrefix(stage, keepUserId)
  const stagePrefix = `${stage}:`
  for (const key of await store.keys('outbox')) {
    if (key.startsWith(keep) || !key.startsWith(stagePrefix)) continue
    if (!proceed()) return
    await store.remove('outbox', key)
  }
}
